export type JobStatus =
  | 'queued'
  | 'preprocessing'
  | 'running'
  | 'aggregating'
  | 'complete'
  | 'partial_complete'
  | 'failed'
  | 'canceled';

export type MediaType = 'image' | 'video';

export type ModuleName =
  | 'metadata'
  | 'container_codec'
  | 'compression'
  | 'fingerprint_noise'
  | 'lighting_geometry'
  | 'lens_behavior'
  | 'ai_artifact'
  | 'video_temporal'
  | 'explanation_synthesis'
  | 'aggregation';

export type ModuleStatus = 'pending' | 'running' | 'success' | 'warning' | 'failed' | 'skipped';

export type Severity = 'high' | 'medium' | 'low' | 'info';

export type FindingCategory =
  | 'metadata_inconsistency'
  | 'container_codec_anomaly'
  | 'compression_artifact_inconsistency'
  | 'fingerprint_noise_inconsistency'
  | 'lighting_physics_inconsistency'
  | 'reflection_or_shadow_inconsistency'
  | 'lens_behavior_inconsistency'
  | 'ai_generation_artifact'
  | 'ai_edit_artifact'
  | 'temporal_inconsistency'
  | 'face_temporal_anomaly'
  | 'semantic_implausibility'
  | 'informational_editing_trace';

export interface MediaAsset {
  id: string;
  jobId: string;
  mediaType: MediaType;
  filename: string;
  mimeType: string;
  sizeBytes: number;
  width?: number;
  height?: number;
  durationMs?: number;
  frameRate?: number;
  codec?: string;
  gcsUri: string;
  createdAt: string;
}

export interface Job {
  id: string;
  status: JobStatus;
  mode: 'standard' | 'deep';
  createdAt: string;
  updatedAt: string;
  mediaAsset?: MediaAsset;
  progressPct: number;
  errorMessage?: string;
  partialResultsAvailable: boolean;
  retentionPolicy: {
    autoDelete: boolean;
    deleteAfterHours?: number;
  };
  riskScore?: number;
  riskBand?: 'low' | 'moderate' | 'high';
  findingsCount?: number;
}

export interface ModuleRun {
  id: string;
  jobId: string;
  moduleName: ModuleName;
  status: ModuleStatus;
  startedAt?: string;
  finishedAt?: string;
  runtimeMs?: number;
  version?: string;
  warningMessage?: string;
  errorMessage?: string;
  outputUri?: string;
}

export interface BoundingBox {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface TimeSpan {
  startMs: number;
  endMs: number;
}

export interface EvidenceRef {
  moduleName: string;
  score?: number;
  artifactUri?: string;
  artifactType?: 'heatmap' | 'crop' | 'json' | 'frame' | 'mask';
  note?: string;
}

export interface Finding {
  id: string;
  jobId: string;
  category: FindingCategory;
  title: string;
  severity: Severity;
  confidence: number;
  mediaScope: 'global' | 'region' | 'time_region';
  region?: BoundingBox;
  frameIndex?: number;
  timeSpan?: TimeSpan;
  trackId?: string;
  explanation: string;
  shortExplanation: string;
  evidence: EvidenceRef[];
  tags: string[];
  createdAt: string;
}

export interface ReportSummary {
  overallRiskScore: number;
  riskBand: 'low' | 'moderate' | 'high';
  conclusionText: string;
  caveats: string[];
}

export interface Report {
  id: string;
  jobId: string;
  version: string;
  summary: ReportSummary;
  findings: Finding[];
  moduleRuns: ModuleRun[];
  metadataSummary: Record<string, unknown>;
  generatedAt: string;
}

export const MODULE_DISPLAY_NAMES: Record<ModuleName, string> = {
  metadata: 'Metadata Inspection',
  container_codec: 'Container/Codec Inspection',
  compression: 'Compression Artifact Analysis',
  fingerprint_noise: 'Sensor Noise / Fingerprint Analysis',
  lighting_geometry: 'Lighting & Geometry Analysis',
  lens_behavior: 'Lens Behavior Analysis',
  ai_artifact: 'AI Artifact Detection',
  video_temporal: 'Temporal Consistency (Video)',
  explanation_synthesis: 'Explanation Synthesis',
  aggregation: 'Aggregation & Localization',
};

export const SEVERITY_COLORS: Record<Severity, string> = {
  high: '#EF4444',
  medium: '#F97316',
  low: '#EAB308',
  info: '#6B7280',
};

export const IMAGE_MODULES: ModuleName[] = [
  'metadata',
  'compression',
  'fingerprint_noise',
  'lighting_geometry',
  'lens_behavior',
  'ai_artifact',
  'explanation_synthesis',
  'aggregation',
];

export const VIDEO_MODULES: ModuleName[] = [
  'metadata',
  'container_codec',
  'compression',
  'fingerprint_noise',
  'lighting_geometry',
  'lens_behavior',
  'ai_artifact',
  'video_temporal',
  'explanation_synthesis',
  'aggregation',
];
